import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import IconStar from 'icons/icon-star';

import { createFavorite, removeFavorite } from 'actions/favorites';

import { makeGetFavoriteByTypeProjectAndResource } from 'selectors/favorites';

import Tooltip from 'components/tooltip';

import { Favorite } from 'types';
import { RootState } from 'reducers';

interface FavoriteToggleProps {
    type: Favorite['type'],
    projectId?: Favorite['project_id'],
    resourceId?: Favorite['resource_id'],
    btnClassName?: string,
    customName?: string
}

const getFavoriteByTypeProjectAndResource = makeGetFavoriteByTypeProjectAndResource();

const FavoriteToggle = ({ type, projectId, resourceId, btnClassName = 'btn btn-medium btn-secondary btn-border btn-square btn-icon', customName }: FavoriteToggleProps) => {
    const { t } = useTranslation(['favorite', 'folder', 'doc', 'file', 'common', 'milestone', 'task', 'chat']);
    const existing = useSelector(({ favorites }: RootState) => getFavoriteByTypeProjectAndResource(favorites.items, type, projectId, resourceId));

    const handleToggle = () => {
        if (existing) {
            removeFavorite(existing.id);
        } else {
            createFavorite({
                type,
                project_id: projectId,
                resource_id: resourceId
            });
        }
    }

    const name = useMemo(() => {
        switch (type) {
            case 'module':
                switch (resourceId) {
                    case 'home':
                        return t('common:Dashboard');

                    case 'milestones':
                        return t('milestone:Roadmap');

                    case 'tasks':
                        return t('task:Tasks');

                    case 'discussion':
                        return t('chat:Discussions');

                    case 'documents':
                        return t('doc:Docs');

                    case 'files':
                        return t('file:Files');

                    default:
                        return '';
                }

            case 'fileFolder':
            case 'docFolder':
                return t('folder:Folder');

            case 'doc':
                return t('doc:Doc');

            case 'file':
                return t('file:File');

            case 'projectEmbed':
                return customName;

            default:
                return '';
        }
    }, [type, customName, resourceId]);

    return (
        <Tooltip tooltip={existing ? t('removeFavorite', { name }) : t('addFavorite', { name }) }>
            <button className={btnClassName + (existing ? ' active' : '')} onClick={handleToggle}>
                <IconStar size="18px" style={existing && { color: 'var(--color-warning)' }} />
            </button>
        </Tooltip>
    );
}

export default FavoriteToggle;
